import type { ComponentType } from 'react';
import type { CluePayload } from '@guess/engine';
import { AudioClue } from './AudioClue';
import { EmojiClue } from './EmojiClue';
import { ImageClue } from './ImageClue';
import { ListClue } from './ListClue';
import { NoClue } from './NoClue';
import { SwatchClue } from './SwatchClue';
import { TextClue } from './TextClue';

/**
 * REGISTRE 1 sur 2 : ce que le joueur PERCOIT.
 *
 * Le serveur n'envoie jamais un mini-jeu, il envoie un `CluePayload` avec un
 * `kind`. Le client n'a donc pas un composant par mini-jeu, il a un composant
 * par FORME d'indice — et un mini-jeu n'est plus qu'un couple (indice, entree)
 * deja couvert.
 *
 * Concretement : « devine le prix » = indice `text` + entree `number`. Les
 * deux briques existent, il n'y a RIEN a ecrire ici. C'est la meme promesse
 * que tient `round.ts` cote moteur, prolongee jusqu'a l'ecran.
 *
 * La garantie est portee par le type, pas par la discipline : `ClueRegistry`
 * exige une entree pour CHAQUE variante de l'union. Ajouter un `kind` dans
 * `types.ts` sans ecrire son rendu ne donne pas un ecran vide en partie, ca
 * donne une erreur de compilation ici.
 */

export interface ClueProps<P extends CluePayload> {
  payload: P;
  /**
   * Change a chaque palier. Les composants s'en servent comme cle
   * d'animation : c'est ce qui fait qu'un nouveau palier SE VOIT, au lieu de
   * modifier l'ecran en silence.
   */
  stageToken: number;
  /**
   * Le chrono de la manche, brut — le meme que celui du Countdown affiche
   * (`useRemaining`), donc deja continu (~60x/s), PAS cale sur les paliers
   * de cote. `totalMs` a 0 hors manche (pas encore de chrono).
   *
   * Un indice qui veut se reveler en continu plutot que par palier de cote
   * (l'image qui se depixelise, par ex.) s'en sert pour calculer sa propre
   * cadence ; les autres l'ignorent simplement.
   */
  elapsedMs: number;
  totalMs: number;
}

type ClueRegistry = {
  [K in CluePayload['kind']]: ComponentType<ClueProps<Extract<CluePayload, { kind: K }>>>;
};

const REGISTRY: ClueRegistry = {
  text: TextClue,
  image: ImageClue,
  audio: AudioClue,
  emoji: EmojiClue,
  swatch: SwatchClue,
  list: ListClue,
  none: NoClue,
};

export function ClueRenderer({ payload, stageToken, elapsedMs, totalMs }: ClueProps<CluePayload>) {
  // L'assertion est confinee a cette ligne, et elle est sure : le registre est
  // indexe par la meme cle que celle qui discrimine l'union. TypeScript ne
  // sait pas relier les deux, mais il a deja verifie que la table est complete.
  const Component = REGISTRY[payload.kind] as ComponentType<ClueProps<CluePayload>>;
  return <Component payload={payload} stageToken={stageToken} elapsedMs={elapsedMs} totalMs={totalMs} />;
}

/** Les formes d'indice connues. Sert au test de completude du registre. */
export const CLUE_KINDS = Object.keys(REGISTRY) as Array<CluePayload['kind']>;
